import { css } from '@emotion/react';
import {
  Anchor,
  AspectRatio,
  Box,
  Button,
  COLOR_PALETTE,
  Flex,
  Group,
  Icon,
  OptimizeImage,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react';
import { faMedal, faPlay } from '@inflearn/pro-solid-svg-icons';
import { useTranslation } from 'next-i18next';

import { styleShowOnlyLargerThanMobile } from '../../../../../styles/commonStyle';
import useMediaQuery from '../../../../../styles/useMediaQuery';

export const COURSE_CARD_THUMBNAIL_RATIO = 3 / 1.95;

type CourseThumbnailProps = {
  image: string;
  alt: string;
  title: string;
  link: string;
  videoAvailable: boolean;
  hasCertificate: boolean;
  isOfflineCourse: boolean;
  onLinkClick?: () => void;
  onClickCertificate?: () => void;
  disabled?: boolean;
};

export function CourseThumbnail({
  alt,
  title,
  link,
  hasCertificate,
  image,
  videoAvailable,
  isOfflineCourse,
  disabled,
  onLinkClick,
  onClickCertificate,
}: CourseThumbnailProps) {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();

  return (
    <Box pos="relative" css={styleCardHover}>
      <Anchor
        href={link}
        onClick={onLinkClick}
        aria-label={t('{{title}} 학습하러 가기', { title: title })}
        css={{
          display: 'block',
        }}
      >
        <AspectRatio
          ratio={COURSE_CARD_THUMBNAIL_RATIO}
          css={{
            opacity: !disabled ? 1 : 0.5,
            border: `1px solid ${COLOR_PALETTE.gray[2]}`,
            borderRadius: 8,
            overflow: 'hidden',
            img: {
              objectFit: 'cover',
            },
          }}
        >
          <OptimizeImage width="100%" height="100%" src={image} alt={alt} w={295} />
        </AspectRatio>
      </Anchor>
      {!isOfflineCourse && videoAvailable && (
        <Flex
          align="center"
          justify="center"
          w={isMobile ? 30 : 42}
          h={isMobile ? 30 : 42}
          css={{
            pointerEvents: 'none',
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            background: COLOR_PALETTE.dark[6],
            borderRadius: '50%',
            svg: { marginLeft: 2 },
          }}
        >
          <Icon size={isMobile ? 'xs' : 'md'} icon={faPlay} color="white" />
        </Flex>
      )}

      <Group
        position="apart"
        css={{
          zIndex: 2,
          position: 'absolute',
          right: 8,
          top: 8,
        }}
      >
        {hasCertificate && (
          <Button
            className="certificate-badge"
            onClick={onClickCertificate}
            leftIcon={<Icon icon={faMedal} size="xs" color="dark" />}
            variant="default"
            color="infgreen"
            size="xs"
            radius="md"
            css={css([
              {
                boxShadow:
                  '0px 7px 7px -5px #0000000A, 0px 10px 15px -5px #0000000D, 0px 1px 3px 0px #0000000D',
              },
              styleShowOnlyLargerThanMobile,
            ])}
          >
            {t('수료증')}
          </Button>
        )}
      </Group>
    </Box>
  );
}

const styleCardHover = css({
  display: 'block',
  borderRadius: 8,
  overflow: 'hidden',
  transition: 'transform 0.3s ease-in-out',
  transform: 'translateY(0)',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: `0px 17px 17px -7px rgba(0, 0, 0, 0.04), 0px 36px 28px -7px rgba(0, 0, 0, 0.05), 0px 1px 3px 0px rgba(0, 0, 0, 0.05)`,

    [desktopFirstMediaQuery.mobile]: {
      transform: 'none',
      boxShadow: 'none',
    },
  },
});
